/**
 * Version metadata of the Terms of Use Addendum, THE single source both consumers import so they
 * cannot drift apart (2026-08-20, following legal advice — the registration T&C acceptance is now a
 * full BE audit trail, `v2_terms_acceptances`):
 *
 *  - `TermsPage` renders `TERMS_EFFECTIVE_LABEL` as its "Effective as of" line;
 *  - the register flow stashes `TERMS_VERSION` at the tick and `useTermsAcceptanceSync` POSTs it to
 *    `POST /api/v1/terms/acceptance` once a session is authenticated.
 *
 * The BE keeps its own append-only registry of published versions (`Application/Terms/TermsOfUse`)
 * and REJECTS unknown labels — so bumping the page without publishing the version server-side fails
 * loudly at recording time instead of minting an unexplainable audit row. **Any wording change to
 * the addendum requires bumping BOTH constants here AND publishing the new version in the BE
 * registry.** The version label is the addendum's effective date (US order on screen, ISO here).
 */
export const TERMS_VERSION = '2026-08-01';
export const TERMS_EFFECTIVE_LABEL = 'August 1, 2026';

/** localStorage key holding a tick made on the register form (pre-auth — there is no account to key
 * a BE row to yet). Written by `RegisterPage`, drained by `useTermsAcceptanceSync`. */
export const PENDING_TERMS_ACCEPTANCE_KEY = 'journal:terms.pendingAcceptance.v1';

export interface PendingTermsAcceptance {
  version: string;
  /** ISO timestamp of the actual tick, sent as `clientAcceptedAt` (context — the BE's own clock is
   * the authoritative `accepted_at`). */
  acceptedAt: string;
}

export function stashPendingTermsAcceptance(): void {
  const pending: PendingTermsAcceptance = { version: TERMS_VERSION, acceptedAt: new Date().toISOString() };
  try {
    localStorage.setItem(PENDING_TERMS_ACCEPTANCE_KEY, JSON.stringify(pending));
  } catch {
    // Storage unavailable (private mode/quota): the tick still gates the form; only the mirror is lost.
  }
}

export function readPendingTermsAcceptance(): PendingTermsAcceptance | null {
  try {
    const raw = localStorage.getItem(PENDING_TERMS_ACCEPTANCE_KEY);
    if (!raw) return null;
    const parsed: unknown = JSON.parse(raw);
    if (typeof parsed !== 'object' || parsed === null) return null;
    const { version, acceptedAt } = parsed as Partial<PendingTermsAcceptance>;
    return typeof version === 'string' && typeof acceptedAt === 'string' ? { version, acceptedAt } : null;
  } catch {
    return null;
  }
}

export function clearPendingTermsAcceptance(): void {
  try {
    localStorage.removeItem(PENDING_TERMS_ACCEPTANCE_KEY);
  } catch {
    // ignore — worst case the next authenticated session retries the POST (append-only trail).
  }
}
